/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AmountString,
  ParseIbanError,
  PaytoString,
  TranslatedString,
  parseIban,
} from "@gnu-taler/taler-util";
import { InternationalizationAPI } from "@gnu-taler/web-util/browser";
/**
 * Extract IBAN from a Payto URI.
 */
export function getIbanFromPayto(url: string): string {
  const pathSplit = new URL(url).pathname.split("/");
  let lastIndex = pathSplit.length - 1;
  // Happens if the path ends with "/".
  if (pathSplit[lastIndex] === "") lastIndex--;
  const iban = pathSplit[lastIndex];
  return iban;
}

export function undefinedIfEmpty<T extends object>(obj: T): T | undefined {
  return Object.keys(obj).some(
    (k) => (obj as Record<string, T>)[k] !== undefined,
  )
    ? obj
    : undefined;
}

export type RecursivePartial<Type> = {
  [P in keyof Type]?: Type[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : Type[P] extends object
      ? RecursivePartial<Type[P]>
      : Type[P];
};
export type ErrorMessageMappingFor<Type> = {
  [prop in keyof Type]+?: Exclude<Type[prop], undefined> extends PaytoString // enumerate known object
    ? TranslatedString
    : Exclude<Type[prop], undefined> extends AmountString
      ? TranslatedString
      : Exclude<Type[prop], undefined> extends TranslatedString
        ? TranslatedString
        : // arrays: every element
          Exclude<Type[prop], undefined> extends (infer U)[]
          ? ErrorMessageMappingFor<U>[]
          : // map: every field
            Exclude<Type[prop], undefined> extends object
            ? ErrorMessageMappingFor<Type[prop]>
            : TranslatedString;
};

export enum TanChannel {
  SMS = "sms",
  EMAIL = "email",
}
export const PAGINATED_LIST_SIZE = 20;
// when doing paginated request, ask for one more
// and use it to know if there are more to request
export const PAGINATED_LIST_REQUEST = PAGINATED_LIST_SIZE + 1;

/**
 * An IBAN is validated by converting it into an integer and performing a
 * basic mod-97 operation (as described in ISO 7064) on it.
 * If the IBAN is valid, the remainder equals 1.
 *
 * The algorithm of IBAN validation is as follows:
 * 1.- Check that the total IBAN length is correct as per the country. If not, the IBAN is invalid
 * 2.- Move the four initial characters to the end of the string
 * 3.- Replace each letter in the string with two digits, thereby expanding the string, where A = 10, B = 11, ..., Z = 35
 * 4.- Interpret the string as a decimal integer and compute the remainder of that number on division by 97
 *
 * If the remainder is 1, the check digit test is passed and the IBAN might be valid.
 *
 */
export function validateIBAN(
  account: string,
  i18n: InternationalizationAPI,
): TranslatedString | undefined {
  const result = parseIban(account);
  if (result.tag === "ok") return undefined;
  switch (result.error) {
    case ParseIbanError.UNSUPPORTED_COUNTRY:
      return i18n.str`IBAN country code not found`;
    case ParseIbanError.TOO_LONG:
      return i18n.str`IBAN numbers have less than 34 digits`;
    case ParseIbanError.TOO_SHORT:
      return i18n.str`IBAN numbers have more than 4 digits`;
    case ParseIbanError.INVALID_LENGTH:
      return i18n.str`IBAN length is invalid for this country`;
    case ParseIbanError.INVALID_CHARSET:
      return i18n.str`The IBAN contains invalid characters`;
    case ParseIbanError.INVALID_CHECKSUM:
      return i18n.str`IBAN number is not valid, checksum is wrong`;
    default:
      return i18n.str`The IBAN is not valid`;
  }
}

export const USERNAME_REGEX = /^[a-zA-Z0-9\-._~]*$/;
// [a-zA-Z0-9\\-\\._~]+
export function validateTalerBank(
  account: string,
  i18n: InternationalizationAPI,
): TranslatedString | undefined {
  if (!USERNAME_REGEX.test(account)) {
    return i18n.str`Use letters, numbers or any of these characters: - . _ ~`;
  }
  return undefined;
}
